import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  loginValidationSchema,
  otpValidationSchema,
} from "../../utils/validations/validations";
import { FormLoginTypes, FormOtpTypes } from "../../interfaces/app_types";
import { useRecoilState } from "recoil";
import { loginResponseState } from "../../utils/atoms/atoms";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { login, verifyOtp, resendOtp } from "../../api/auth";

const LoginPage = () => {
  const [step, setStep] = useState("credentials");
  const [loginResponse, setLoginResponse] = useRecoilState(loginResponseState);
  const [resendMessage, setResendMessage] = useState("");
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormLoginTypes>({
    resolver: yupResolver(loginValidationSchema),
  });

  const {
    register: registerOtp,
    handleSubmit: handleOtpSubmit,
    formState: { errors: otpErrors },
    reset: resetOtp,
  } = useForm<FormOtpTypes>({
    resolver: yupResolver(otpValidationSchema),
  });

  const loginMutation = useMutation({
    mutationFn: (data: FormLoginTypes) => login(data), // ✅ plain object
    onError: () => setLoginResponse("Something went wrong. Please try again."),
    onSuccess: (data: any) => {
      if (data?.otp_required) {
        setLoginResponse("");
        setStep("otp");
      }
    },
  });

  const otpMutation = useMutation({
    mutationFn: async (otp: string) => verifyOtp(otp),
    onError: (error: any) => {
      const message =
        error?.response?.data?.error || "Invalid OTP. Please try again.";
      setLoginResponse(message);
      if (error?.response?.status === 429) {
        setStep("credentials");
        resetOtp();
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["user"]);
      navigate("/todos", { replace: true });
    },
  });

  const resendMutation = useMutation({
    mutationFn: resendOtp,
    onError: (error: any) => {
      const message = error?.response?.data?.error || "Failed to resend OTP.";
      setResendMessage(message);
      if (error?.response?.status === 429) {
        setStep("credentials");
      }
    },
    onSuccess: () => {
      setResendMessage("OTP resent! Check your email.");
      resetOtp();
    },
  });

  if (step === "credentials") {
    return (
      <form
        onSubmit={handleSubmit((data, event) => {
          event?.preventDefault();
          setLoginResponse("");
          loginMutation.mutate(data);
        })}
      >
        <div className="center-outside-div">
          <div className="login-container">
            <div className="login-brand">
              <span className="brand-mark">P</span>
              <div className="heading-1">Pressco</div>
            </div>
            <hr />
            <p className="login-eyebrow">Operations workspace</p>
            <div className="heading-2">Welcome back</div>
            <p className="login-subtitle">
              Sign in to manage your production workflow.
            </p>

            <div className="login-entry">
              <label className="heading-3" htmlFor="login-email">
                Email
              </label>
              <input
                id="login-email"
                {...register("email")}
                placeholder="Email"
                className="form-control"
              />
              <div className="text-danger">{errors?.email?.message}</div>
            </div>

            <div className="login-entry">
              <label className="heading-3" htmlFor="login-password">
                Password
              </label>
              <input
                id="login-password"
                {...register("password")}
                type="password"
                placeholder="Password"
              />
              <div className="text-danger">{errors?.password?.message}</div>
            </div>

            <div className="text-danger">{loginResponse}</div>

            <div className="login-actions">
              <button
                type="submit"
                className="styled-btn--dark--bold"
                disabled={loginMutation.isLoading}
              >
                {loginMutation.isLoading
                  ? "Sending OTP..."
                  : "Continue securely"}
                {!loginMutation.isLoading && (
                  <i className="bi bi-arrow-right"></i>
                )}
              </button>
            </div>
            <div className="login-footer">
              Secure access for Pressco team members
            </div>
          </div>
        </div>
      </form>
    );
  }

  return (
    <form
      onSubmit={handleOtpSubmit((data, event) => {
        event?.preventDefault();
        setLoginResponse("");
        setResendMessage("");
        otpMutation.mutate(data.otp);
      })}
    >
      <div className="center-outside-div">
        <div className="login-container">
          <div className="login-brand">
            <span className="brand-mark">P</span>
            <div className="heading-1">Pressco</div>
          </div>
          <hr />
          <p className="login-eyebrow">Security check</p>
          <div className="heading-2">Enter verification code</div>
          <p className="login-subtitle">
            A 6-digit code was sent to your email.
          </p>

          <div className="login-entry">
            <label className="heading-3" htmlFor="login-otp">
              OTP code
            </label>
            <div style={{ display: step === "otp" ? "block" : "none" }}>
              <input
                id="login-otp"
                {...registerOtp("otp")}
                placeholder="000000"
                maxLength={6}
                inputMode="numeric"
                type="text"
                autoComplete="one-time-code"
                autoFocus
              />
            </div>

            <div className="text-danger">{otpErrors?.otp?.message}</div>
          </div>

          <div className="text-danger">{loginResponse}</div>
          <div className="text-success">{resendMessage}</div>

          <div className="login-actions">
            <button
              type="submit"
              className="styled-btn--dark--bold"
              disabled={otpMutation.isLoading}
            >
              {otpMutation.isLoading ? "Verifying..." : "Verify code"}
            </button>

            <button
              type="button"
              onClick={() => {
                setResendMessage("");
                resendMutation.mutate();
              }}
              className="styled-btn--std"
              disabled={resendMutation.isLoading}
            >
              {resendMutation.isLoading ? "Resending..." : "Resend"}
            </button>

            <button
              type="button"
              className="styled-btn--std"
              onClick={() => {
                setStep("credentials");
                setLoginResponse("");
                setResendMessage("");
              }}
            >
              ← Back
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default LoginPage;
